/**
 * Leadership Page Component
 *
 * Displays information about NCESP leadership and board members
 */

import React, { useState } from 'react'
import { Box, Container, Typography } from '@mui/material'
import Grid from '@mui/material/Grid2'
import { officers, Officer } from '@/data/officers'
import { OfficerCard } from '@/components/OfficerCard'
import { OfficerBioDialog } from '@/components/OfficerBioDialog'
import SEOMetaTags from '@/components/SEOMetaTags'
import { getSEOConfig, getCanonicalUrl } from '@/utils/seo'
import { generatePersonSchema } from '@/utils/schema'

/**
 * Leadership Page Component
 */
const Leadership: React.FC = () => {
  // Get SEO configuration for this page
  const seoConfig = getSEOConfig('/leadership')!
  const canonicalUrl = getCanonicalUrl('/leadership')

  // Generate Person schemas for all officers
  const personSchemas = officers.map(officer =>
    generatePersonSchema({
      id: officer.id,
      name: officer.name,
      title: officer.title,
      bio: officer.biography,
      socialLinks: [], // Officers data doesn't include social links currently
    })
  )

  const [selectedOfficer, setSelectedOfficer] = useState<Officer | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  const handleReadBio = (officer: Officer) => {
    setSelectedOfficer(officer)
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    // Small delay before clearing selected officer for smooth animation
    setTimeout(() => setSelectedOfficer(null), 300)
  }

  return (
    <Box component="main" role="main">
      {/* SEO Meta Tags */}
      <SEOMetaTags
        title={seoConfig.title}
        description={seoConfig.description}
        canonical={canonicalUrl}
        ogImage={seoConfig.ogImage}
        keywords={seoConfig.keywords}
        noIndex={seoConfig.noIndex}
        schemas={personSchemas}
      />
      {/* Leadership Section */}
      <Box
        component="section"
        aria-labelledby="leadership-title"
        sx={{
          py: { xs: 6, md: 8 },
          bgcolor: 'grey.50',
        }}
      >
        <Container maxWidth="lg">
          {/* Section Title */}
          <Typography
            id="leadership-title"
            variant="h2"
            component="h2"
            align="center"
            gutterBottom
            sx={{
              mt: { xs: '1rem' },
              mb: 6,
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.5rem' },
              color: 'primary.main',
            }}
          >
            Leadership
          </Typography>

          {/* Officers Grid */}
          <Grid container spacing={4}>
            {officers.map(officer => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={officer.id}>
                <OfficerCard officer={officer} onReadBio={handleReadBio} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Biography Dialog */}
      <OfficerBioDialog
        officer={selectedOfficer}
        open={dialogOpen}
        onClose={handleCloseDialog}
      />
    </Box>
  )
}

export default Leadership
